"use client";

import { useCallback, useSyncExternalStore } from "react";

const UNSEEN = Symbol("useElementById.unseen");

function subscribeToElementById(
	id: string,
	onStoreChange: () => void,
): () => void {
	let disposed = false;
	let last: HTMLElement | null | typeof UNSEEN = UNSEEN;

	const notifyIfChanged = () => {
		if (disposed) {
			return;
		}
		const next = document.getElementById(id);
		if (last !== UNSEEN && Object.is(next, last)) {
			return;
		}
		last = next;
		onStoreChange();
	};

	queueMicrotask(notifyIfChanged);

	const observer = new MutationObserver(notifyIfChanged);
	observer.observe(document.documentElement, {
		childList: true,
		subtree: true,
		attributes: true,
		attributeFilter: ["id"],
	});

	return () => {
		disposed = true;
		observer.disconnect();
	};
}

export function useElementById(id: string): HTMLElement | null {
	const subscribe = useCallback(
		(onStoreChange: () => void) => subscribeToElementById(id, onStoreChange),
		[id],
	);

	return useSyncExternalStore(
		subscribe,
		() => document.getElementById(id),
		() => null,
	);
}
